import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { KanbanLog } from '@entities/kanban-comment'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { KanbanLogField } from '@entities/kanban-comment'
import { useListsStore } from '@shared/model'
import { useProfileStore } from '@entities/viewer'
import { useDayjs } from '@shared/lib/format'

interface TutorKanbanLogExitReturn {
  refIsExitField: ComputedRef<boolean>
  refLabel: ComputedRef<string>
  refOldValue: ComputedRef<string | null>
  refValue: ComputedRef<string | null>
}

const EXIT_FIELD_LABELS: Record<string, string> = {
  [KanbanLogField.ExitRequestDate]: 'change exit request date',
  [KanbanLogField.ExitDate]: 'change final day',
  [KanbanLogField.ExitReason]: 'change tutor\'s exit reason',
}

export const useTutorKanbanLogExit = (refLog: ComputedRef<KanbanLog>): TutorKanbanLogExitReturn => {
  const listsStore = useListsStore()
  const profileStore = useProfileStore()
  const dayjs = useDayjs()

  const formatRequestDate = (raw: string): string => {
    const date = dayjs.utc(raw)
    return date.isValid() ? date.tz(profileStore.getTimezone).format(DateFormat.KanbanDate) : raw
  }

  const formatFinalDay = (raw: string): string => {
    const date = dayjs(raw.slice(0, DateFormat.DefaultDate.length))
    return date.isValid() ? date.format(DateFormat.FileDate) : raw
  }

  const formatReason = (raw: string): string => listsStore.getLists?.tutor_exit_reason?.[raw] ?? raw

  const formatValue = (raw: string | null): string | null => {
    if (!raw) return null

    switch (refLog.value.field) {
      case KanbanLogField.ExitRequestDate:
        return formatRequestDate(raw)
      case KanbanLogField.ExitDate:
        return formatFinalDay(raw)
      case KanbanLogField.ExitReason:
        return formatReason(raw)
      default:
        return raw
    }
  }

  const refIsExitField = computed<boolean>(() => !!refLog.value.field && refLog.value.field in EXIT_FIELD_LABELS)

  const refLabel = computed<string>(() => EXIT_FIELD_LABELS[refLog.value.field ?? ''] ?? '')

  const refOldValue = computed<string | null>(() => formatValue(refLog.value.old_value))

  const refValue = computed<string | null>(() => formatValue(refLog.value.value))

  return {
    refIsExitField,
    refLabel,
    refOldValue,
    refValue,
  }
}
